import React from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {useAppSelector} from '../store/hooks';
import {
  authLoadingSelector,
  isAuthenticatedSelector,
  needsPasswordSetupSelector,
} from '../store/selectors/auth.selectors';
import LoadingPageComponent from '../utils/loadingPage.component';

const SET_PASSWORD_PATH = '/set-password';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * Gate for the app routes. Public routes (landing, docs, terms, contact, auth) must never be
 * wrapped in this — they have to render for signed-out visitors.
 *
 * While the stored session is being restored we show the loading screen rather than redirecting,
 * otherwise a signed-in user who deep-links to an app URL gets bounced to /login and loses it.
 *
 * Two gates, in order. The second one catches accounts provisioned from a Paddle checkout, which
 * have no password at all: they arrive on a one-time login link, and until they choose one that link
 * is the only way back in. So the set-password screen comes before the rest of the app rather than
 * being something to get around to.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children}) => {
  const location = useLocation();
  const loading = useAppSelector(authLoadingSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);
  const needsPassword = useAppSelector(needsPasswordSetupSelector);

  if (loading) {
    return <LoadingPageComponent loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{from: location.pathname + location.search}} />;
  }

  if (needsPassword && location.pathname !== SET_PASSWORD_PATH) {
    return <Navigate to={SET_PASSWORD_PATH} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
